/* eslint-disable react/prop-types */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  InputNumber,
  Calendar,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { CheckCircleOutlined } from '@ant-design/icons';
import ModalConfirmation from 'components/ModalConfirmation';
import {
  fechasName, ModelRegistroGuardiasFechas, numeroDiasName, urlAgregarGuardias,
  getFechasOcupadasPorVacacion,
} from 'api/configuraciones/ConfigurarGuardiasVacacionales/RegistroGuardias/RegistroGuardiasFechas';
import moment from 'moment';
import dayjs from 'dayjs';

const RegistroGuardiasFechas = ({
  permission, parentId, formParent, dates, disableFields,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState();
  const [open, setVisible] = useState();
  const [visibleAlert, setVisibleAlert] = useState();
  const [estados, setEstados] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);
  const form = formParent;

  const [selectedDates, setSelectedDates] = useState([]); // moment[]
  const [calendarValue, setCalendarValue] = useState(dates?.length
    ? moment(dates[0]?.id)
    : moment());

  // Días que el empleado ya tiene registrados como Vacación disfrutada: un mismo
  // día no puede ser a la vez Guardia y Vacación, así que se bloquean en este
  // calendario (ver getFechasOcupadasPorVacacion).
  const [fechasOcupadasPorVacacion, setFechasOcupadasPorVacacion] = useState([]);
  const empleadoId = form.getFieldValue('empleado');

  useEffect(() => {
    let mounted = true;
    if (!empleadoId) {
      setFechasOcupadasPorVacacion([]);
      return undefined;
    }
    getFechasOcupadasPorVacacion(empleadoId).then((_fechas) => {
      if (mounted) setFechasOcupadasPorVacacion(_fechas || []);
    });
    return () => { mounted = false; };
  }, [empleadoId]);

  const registroName = `${selectedData?.fecha_registro}`;

  useEffect(() => {
    if (!selectedDates) return;

    form.setFieldValue(numeroDiasName, selectedDates.length);
  }, [selectedDates]);

  const {
    cliente,
    nameModel,
    nameModelSingular,
  } = ModelRegistroGuardiasFechas;

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await cliente.obtenerListaDeRegistros(
      { registro_dias_guardia: parentId },
    );
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        cliente.obtenerListaDeRegistros(
          { registro_dias_guardia: parentId },
        ),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    // setSelectedDates([]);
    setCalendarValue(dates?.length
      ? moment(dates[0]?.id)
      : moment());
    setDisableForm(false);
    setVisible(false);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Autorizar un Tipo de Checado ya Autorizado');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await cliente.actualizarRegistro(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableForm(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _motivoPermiso = await cliente.obtenerUnRegistro(match.id);
    setTimeout(() => {
      if (_motivoPermiso) {
        // form.resetFields();
        setTimeout(() => {
          // isDisabled(_motivoPermiso?.estados_globales);
          form.setFieldsValue(_motivoPermiso);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };
  const onClickAdd = () => {
    onCancel();
    const fechas = data.map((value) => value?.fecha_registro);
    form.setFieldValue(fechasName, fechas);
    setSelectedDates(fechas?.map((fecha) => moment(fecha)));
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const { fechas, ...values } = form.getFieldsValue();
      // const [key] = selectedRowKeys;
      // Crear payload con formato { fecha: 'YYYY-MM-DD' }
      const payloadFechas = fechas.map((_fecha) => ({
        fecha: _fecha,
      }));
      const saved = await cliente.reemplazarRegistro(parentId, payloadFechas, urlAgregarGuardias);
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await cliente.borrarRegistro(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Fecha',
      dataIndex: 'fecha_registro',
      key: 'fecha_registro',
      width: 250,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  /// Calendar select (add / remove)
  const onSelectDate = (date) => {
    if (!date.isSame(calendarValue, 'month')) return;
    setSelectedDates((prev) => {
      const exists = prev.some((d) => d.isSame(date, 'day'));
      const next = exists
        ? prev.filter((d) => !d.isSame(date, 'day'))
        : [...prev, date];

      const fechas = next.map((d) => d.format('YYYY-MM-DD'));

      form.setFieldValue(fechasName, fechas);

      return next;
    });
  };

  // Calendar highlight
  const dateCellRender = (value) => {
    const cellDate = moment(value); //  convert dayjs → moment

    const isSelected = selectedDates.some((d) => d.isSame(cellDate, 'day'));
    const isOcupadaPorVacacion = fechasOcupadasPorVacacion.includes(cellDate.format('YYYY-MM-DD'));
    if (isOcupadaPorVacacion) {
      return (
        <div
          title="Ya registrado como día de Vacaciones disfrutadas"
          style={{
            height: '100%',
            backgroundColor: '#1890ff33',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
          }}
        >
          {value.date()}
        </div>
      );
    }
    return isSelected ? (
      <div
        title="Día de Guardia"
        style={{
          height: '100%',
          backgroundColor: '#52c41a33',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
        }}
      >
        {value.date()}
      </div>
    ) : null;
  };
  const disabledDate = (current) => {
    if (!current) return true;

    const key = current.format('YYYY-MM-DD');
    if (fechasOcupadasPorVacacion.includes(key)) return true;
    // true = deshabilitado
    return !dates?.map((value) => value?.id)?.includes(key);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            // onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          customActions={[
            {/*
              onClick: () => setVisibleAuthConfirmation(true),
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys?.length || disableAuth,
            */},
          ]}
          disableDelete={
            disableFields
          }
          allowExportParam={false}
          allowSearch={false}
        />
        <Modal
          open={open}
          closable={false}
          destroyOnClose={false}
          maskClosable={!loading}
          footer={null}
          width="50%"
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${nameModel}`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            disabled={disableForm}
          >
            <Col xs={24} md={16}>
              <Calendar
                fullscreen={false}
                value={calendarValue}
                onSelect={onSelectDate}
                onPanelChange={(val) => setCalendarValue(val)}
                dateCellRender={dateCellRender}
                disabledDate={disableFields ? () => true : disabledDate}
              />
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  marginTop: 8,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: '#52c41a66',
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Días de Guardia</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: '#1890ff66',
                    }}
                  />
                  <span style={{ fontSize: 12 }}>Vacaciones Disfrutadas</span>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={24}>
              <Form.Item
                name={fechasName}
                label="Fecha de Captura"
              >
                <Select
                  mode="multiple"
                  dataSource={dates}
                  /* onChange={(value) => {
                    if (!value) { setCalendarValue([]); }
                    const dateValues = value?.map((fecha) => moment(fecha));
                    setSelectedDates(dateValues);
                  }} */
                  render={(value) => value?.fecha?.format('DD-MM-YYYY')}
                  disabled
                />
                {/*
                <DatePicker
                  multiple
                  placeholder=""
                  format="DD/MM/YYYY"
                  disabledDate={multipleDatePicker.disabledDateByRange(
                    fechaInicio,
                    fechaFin,
                    true,
                  )}
                  onChange={(date) => {
                    if (!date) return;
                    const _format = 'DD-MM-YYYY';
                    const params = {
                      date,
                      selectedDates,
                    };
                    const result = multipleDatePicker.multipleSelect(params);
                    setSelectedDates(result.selectedDates);
                    form.setFieldValue('dias_guardia', result.selectedDates.map(
                    (d) => d.format(_format)).join(','));
                    form.setFieldValue('numero_dias', result.selectedDates.length);
                  }}
                />
                */}
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name={numeroDiasName}
                label="No. de Días"
                rules={rules.required}
                hasFeedback
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`${nameModelSingular} ${registroName}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Autorizar ${nameModelSingular} ${registroName}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Inactivar ${nameModelSingular} ${registroName}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default RegistroGuardiasFechas;
